import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AccessTimePatternCard } from '../../../components/accessTimePattern/AccessTimePatternCard';
import { AccessTimePatternDetails } from '../../../components/accessTimePattern/AccessTimePatternDetails';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { toast } from "react-toastify";
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { AccessTimePattern as AccessTimePatternModel } from '../../../models/AccessTimePattern';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { TabSummary } from '../../../models/TabSummary';
import { getAccessTimePatternById, getAccessTimePatternNamesWithPagination, storeAccessTimePattern } from '../../../services/accessTimePatternService';
import { calculateDuration, getTime, setTime } from '../../../services/utilities/dateFormatting';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { hideColumns, removeColumns } from '../../../models/utilities/columnHelper';
import { ListQuery } from '../../../models/ListQuery';
import { assignUnassignAccessGroups, getAccessGroupsNamesWithPagination, getAllAccessGroups } from '../../../services/accessGroupService';
import AccessGroupColumns from '../../../models/ColumnDefinitions/AccessGroupColumns';
import AccessGroupSearch from '../../../models/search/AccessGroupSearch';
import { Lookup } from '../../../models/Lookup';
import { appendFilter } from '../../../utilities/filters';
import AccessTimePatternSearch from '../../../models/search/AccessTimePatternSearch';
import { getAccessAreaNamesWithPagination } from '../../../services/accessAreaService';
import LookupTypes from '../../../models/LookupTypes';
import AccessAreaSearch from '../../../models/search/AccessAreaSearch';
import { getSiteNamesWithPagination } from '../../../services/siteService';
import SiteSearch from '../../../models/search/SiteSearch';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { getDayNames } from '../../../utilities/DateUtils';
import AssignedTab from '@/components/assignWizard/AssignedTab';
import { Button } from '@/components/button/Button';
import AssignModalTwo from '@/components/assignWizard/AssignModal';
import { ACCESS_TYPES } from '@/constants/access-constants';
import { Name } from '@/models/Name';
import { GROUP_TYPE } from '@/constants/area-constants';
import { LICENSE_APERIO_OSS } from "../../../constants/license-constants";
import { useGlobalState } from "../../../Providers";
import { PaginationModel } from '@/models/Pagination';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/enrolment/access-time-patterns';
const itemPath = '/access-control/enrolment/access-time-pattern/';
const accessGroupName = "agName";

const initialItem = {
    id: 0,
    name: '',
    days: '',
    startTime: '',
    duration: '',
    site: '',
    siteId: 0,
    dayList: new Array(0),
    endTime: '',
    default: undefined,
    endTimeExtract: '',
}

const AccessTimePattern: FC = () => {
    const { buildInfo } = useGlobalState() ?? {};
    const isLicensedForAperioOss = buildInfo?.licenceIds?.includes(LICENSE_APERIO_OSS);
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.accessGroup), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.floorGroup), badge: 0, showInitial: true, isActive: true },
        ...(isLicensedForAperioOss ? [{ label: translate.byKey(TranslationKeys.ossGroup), badge: 0, showInitial: true, isActive: true }] : []),
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const site = useSelector((state: IAppState) => state.site.site);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<AccessTimePatternModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const [showAddModal, setShowAddModal] = useState(false);
    const [beingAssigned, setBeingAssigned] = useState<GROUP_TYPE>(1)
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    const getDataMethod = (groupType: GROUP_TYPE ,listParameters?: ListQuery) => {
        return getAllAccessGroups(groupType, translate, {
            ...listParameters,
            filters: appendFilter(itemId, "Access Time Pattern", "accessTimePattern.id", listParameters?.filters ?? []),
        });
    };

    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find((item) => item.accessor === "name")?.searchOnName,
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.accessTimePattern,
            values: [],
            dataMethod: getAccessTimePatternNamesWithPagination,
            searchAccessor: AccessTimePatternSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.days,
            values: getDayNames(translate)
        }
    ];

    const newGroupsColumns = AccessGroupColumns.map((column) => {
        return {
            ...column,
            active: column.accessor === accessGroupName ? false : column.active,
        };
    });

    const newAccessGroupsSearch = AccessGroupSearch.filter((column) => {
        return column.accessor !== accessGroupName;
    });

    const handleManageClick = (groupType: GROUP_TYPE) => {
        // Don't allow OSS group management if not licensed
        if (groupType === 3 && !isLicensedForAperioOss) {
            return;
        }
        setBeingAssigned(groupType);
        setShowAddModal(true);
    }

    /**
     * Content and descriptors for each tab
     */
    const tabContentList: ContentWrapper[] = [
        {
            content: <AccessTimePatternDetails />
        },
        {
            content: <AssignedTab
                key={`assignedAccessGroupsFor${itemId}`}
                column={removeColumns(newGroupsColumns, ['enrolments', 'accessProfile'])}
                searchColumn={newAccessGroupsSearch}
                id={itemId}
                dataMethod={(listParameters?: ListQuery) => getDataMethod(1, listParameters)}
                title={translate.byKey(TranslationKeys.assignedAccessGroups)}
                lookup={lookup}>
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => handleManageClick(1)}>{translate.byKey(TranslationKeys.manage)}</Button>
            </AssignedTab>
        },
        {
            content: <AssignedTab
                key={`assignedFloorGroupsFor${itemId}`}
                column={removeColumns(newGroupsColumns, ['enrolments', 'accessProfile'])}
                searchColumn={newAccessGroupsSearch}
                id={itemId}
                dataMethod={(listParameters?: ListQuery) => getDataMethod(2, listParameters)}
                title={translate.byKey(TranslationKeys.assignedFloorGroups)}
                lookup={lookup}>
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => handleManageClick(2)}>{translate.byKey(TranslationKeys.manage)}</Button>
            </AssignedTab>
        },
        ...isLicensedForAperioOss ? [{
            content: <AssignedTab
                key={`assignedOssGroupsFor${itemId}`}
                column={removeColumns(newGroupsColumns, ['area', 'enrolments', 'accessProfile'])}
                searchColumn={newAccessGroupsSearch}
                id={itemId}
                dataMethod={(listParameters?: ListQuery) => getDataMethod(3, listParameters)}
                title={translate.byKey(TranslationKeys.assignedOssGroups)}
                lookup={lookup}>
                <Button variant="submit" shape="rounded" className="py-1 px-5" onClick={() => handleManageClick(3)}>{translate.byKey(TranslationKeys.manage)}</Button>
            </AssignedTab>
        }] : []
    ];

    /**
    * Fetch all topics for the topic list component
    * @returns
    */
    const topic: Topic = {
        accessor: 'atpName',
        allItemsPath,
        itemPath,
        title: translate.byKey(TranslationKeys.accessTimePatterns),
    }

    /**
     * Fetch all the required data on a re-render
     */
    const getAssignedItems = async (
        setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
        setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, 
        setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>,
        setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>,
        setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>,
        setAlwaysSelectedAndDisabled?: React.Dispatch<React.SetStateAction<string[]>>) => {

        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = []
            do {
                const accessGroups = (await getAccessGroupsNamesWithPagination(beingAssigned, { filters: itemId ? appendFilter(itemId, 'Access Time Pattern', 'accessTimePattern.id', []) : [], pageSize: 100, goToPage: pageNumber }));
                ids = ids.concat(accessGroups.rows.map(accessGroup => accessGroup.value.toString()));
                rows = rows.concat(accessGroups.rows.map(row => ({ id: row.value, name: row.label })));
                pagination = accessGroups.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages)

            setSelectedIds(ids);
            setSelectedRows(rows);
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
            setAlwaysSelectedAndDisabled && setAlwaysSelectedAndDisabled(ids);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusyLoader(false);
        }
    }

    const reloadPage = async () => {
        try {
            setTabs([]);
            let newAccessTimePattern: any;
            if (itemId) {
                newAccessTimePattern = await getAccessTimePatternById(itemId, translate);
                // this is temporary. the model properties should reflect the type. what's happening now is the model is reflecting what a specific page needs to display, and that makes the model less reusable
                if (newAccessTimePattern.startTime !== '--:--') {
                    setSelectedItem({label: newAccessTimePattern.name, value: newAccessTimePattern.id.toString(), isDefault: newAccessTimePattern.isDefault})
                    setCurrentItem({ ...newAccessTimePattern, startTime: setTime(newAccessTimePattern.startTime), endTime: setTime(newAccessTimePattern.endTime) });
                }
                else {
                    setSelectedItem({label: newAccessTimePattern.name, value: newAccessTimePattern.id.toString(), isDefault: newAccessTimePattern.isDefault})
                    setCurrentItem({ ...newAccessTimePattern, startTime: '', endTime: '', duration: "24hh:mm" });
                }
                const updatedTabs = updateTabBadgeTotal(1, newAccessTimePattern.accessGroups, 
                    updateTabBadgeTotal(2, newAccessTimePattern.floorGroups, 
                        isLicensedForAperioOss ? updateTabBadgeTotal(3, newAccessTimePattern.ossGroups, initialTabs) : initialTabs));
                setTabs(updatedTabs);
            }
            else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[0]]);
            }
            setBreadcrumb([
                { value: '/access-control/enrolment', displayName: translate.byKey(TranslationKeys.enrolments) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.accessTimePatterns) },
                { value: itemPath + `${itemId}`, displayName: `${newAccessTimePattern?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    /**
     * Save details when the form is submitted
     * @param values
     */
    const handleSubmit = (toBeAssigned: string[], toBeUnassigned: string[]) => {
        setBusy(true);
        assignUnassignAccessGroups(toBeAssigned, toBeUnassigned, itemId, "accessTimePattern").then(() => {
            toast.success(translate.byKey(TranslationKeys.success));
            reloadPage();
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            setBusy(false);
        });
    }

    const saveAccessTimePattern = (values: any) => {
        const accessTimePattern: AccessTimePatternModel = {
            ...currentItem,
            id: +itemId,
            name: values.name,
            dayList: values.dayList,
            startTime: values.startTime ? getTime(values.startTime) : '0',
            duration: values.startTime && values.endTime ? calculateDuration(values.startTime, values.endTime) : '2400',
            siteId: itemId ? values.siteId : site.value,
            site: itemId ? values.site : site.label,
            days: '',
            endTime: values.endTime ? getTime(values.endTime) : '2400',
            endTimeExtract: values.endTime ? getTime(values.endTime) : '2400',
        };
        const createNewAccessTimePattern = itemId ? +itemId < 0 : true;

        setBusy(true);
        storeAccessTimePattern(accessTimePattern, createNewAccessTimePattern).then(async (value) => {
            setItemId(value.id);
            history.push(`${itemPath}${value.id}`);
            reloadPage();
            toast.success(translate.byKey(TranslationKeys.accessTimePatternSaved));
        }).catch(failure => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
        }).finally(() => {
            setBusy(false);
        });
    }
    
    // <--
    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    /**
     *
     * @returns Inner content block
     */
    const getTabContent = (index?: number) => {
        if (itemId && tabs[index ?? 0].isActive) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }
        return tabContentList[0].content;
    };

    const getModalDataMethod = (listParameters?: ListQuery) => {
        return getAllAccessGroups(beingAssigned, translate, {
            ...listParameters,
            filters: appendFilter(currentItem?.siteId, "Site", "accessTimePattern.site.id",listParameters?.filters ?? []),
        });
    };
    
    return <div className='flex h-full'>
        <TopicContextProvider dataMethod={getAccessTimePatternNamesWithPagination} itemId={itemId} topic={topic} selectedItem={selectedItem} >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    itemPath={itemPath}
                    saveDetails={saveAccessTimePattern}
                    tabs={tabs}
                    initialValues={currentItem}>
                    <AccessTimePatternCard />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
        {(beingAssigned !== 3 || isLicensedForAperioOss) && (
            <AssignModalTwo
                key={`assignAccessGroupsFor${itemId}`}
                assigningTitle={beingAssigned === ACCESS_TYPES.ACCESS_GROUP ? translate.byKey(TranslationKeys.accessGroups) : beingAssigned === ACCESS_TYPES.FLOOR_GROUP ? translate.byKey(TranslationKeys.floorGroups) : translate.byKey(TranslationKeys.ossGroups)}
                isOpen={showAddModal}
                lookup={lookup}
                dataMethod={getModalDataMethod}
                onClose={() => { setShowAddModal(false) }}
                modalTitle={beingAssigned === ACCESS_TYPES.ACCESS_GROUP ? translate.byKey(TranslationKeys.assignAccessGroups) : beingAssigned === ACCESS_TYPES.FLOOR_GROUP ? translate.byKey(TranslationKeys.assignFloorGroups) : translate.byKey(TranslationKeys.assignOssGroups)}
                searchColumns={AccessGroupSearch}
                tableColumns={hideColumns(AccessGroupColumns, ["days", "startTime", "duration", "enrolments", "accessTimePatterns"])}
                getAssignedItems={getAssignedItems}
                handleSubmit={handleSubmit}/>
        )}
    </div >
}

export default AccessTimePattern;
